// Theme selection. Two palettes live in src/app/styles/tokens.css: `hearth`
// (light, the default) and `moss` (dark).
//
// The user's preference has three states, but <html data-theme> only ever holds
// a resolved one — 'auto' is resolved here in JS. That is what lets tokens.css
// carry the moss palette exactly once, instead of once under the attribute and
// again inside a prefers-color-scheme query.
//
// The preference lives in localStorage rather than the app's own storage layer
// (src/core/storage) for three reasons: setupStorage() awaits authentication
// before any setting is readable, so a backend-stored theme would guarantee a
// flash on the sign-in screen; db.setSetting issues a PocketBase write whose
// failure surfaces as "Progress didn't save", which is the wrong severity for a
// display preference; and theme is a per-device choice that should not sync.
// `syrinx.packBaseUrl` sets the precedent for the key namespace.
//
// This module must stay under src/app/ — importing it from src/core/** would
// break the node-environment vitest suite by touching document/localStorage.

export type ThemePref = 'auto' | 'hearth' | 'moss';
export type Theme = 'hearth' | 'moss';

const KEY = 'syrinx.theme';

/** Pure, and the only branching logic in the theme system. */
export function resolveTheme(pref: ThemePref, prefersDark: boolean): Theme {
  if (pref === 'moss') return 'moss';
  if (pref === 'hearth') return 'hearth';
  return prefersDark ? 'moss' : 'hearth';
}

export function getThemePref(): ThemePref {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === 'hearth' || raw === 'moss' || raw === 'auto') return raw;
  } catch {
    // Private mode / blocked storage — fall through to the default.
  }
  return 'auto';
}

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** The theme actually on screen right now. */
export function activeTheme(): Theme {
  return document.documentElement.dataset.theme === 'moss' ? 'moss' : 'hearth';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  syncThemeColor();
}

export function setThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    // Not fatal: the theme still applies for this session.
  }
  applyTheme(resolveTheme(pref, prefersDark()));
}

/**
 * Point <meta name="theme-color"> at whatever --chrome currently resolves to,
 * which keeps tokens.css the single source of truth after boot. (Custom
 * properties are substituted at computed-value time, so this reads a literal.)
 *
 * Known limitation: iOS Safari in standalone mode samples theme-color at load
 * and does not reliably repaint on mutation. Android and desktop do.
 */
export function syncThemeColor(): void {
  const chrome = getComputedStyle(document.documentElement)
    .getPropertyValue('--chrome')
    .trim();
  if (!chrome) return;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', chrome);
}

/**
 * Follow the OS if — and only if — the user hasn't picked a side. The attribute
 * is already correct at this point (index.html sets it synchronously); this
 * only handles the OS flipping mid-session.
 */
export function watchSystemTheme(): void {
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', (e) => {
      if (getThemePref() === 'auto') applyTheme(e.matches ? 'moss' : 'hearth');
    });
}
